
import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { TrendingUp, BarChart2, Settings, Users } from 'lucide-react';
import { InvestmentGoalsDialog } from '@/components/trading/InvestmentGoalsDialog';
import { useNavigate } from 'react-router-dom';
import { cn } from "@/lib/utils";

type ActionWithDialog = {
  id: string;
  title: string;
  description: string;
  icon: React.ComponentType<any>;
  variant: 'purple' | 'blue' | 'green' | 'orange';
  action: 'openDialog';
  path?: never; // Path is not needed when action is 'openDialog'
  comingSoon?: boolean;
};

type ActionWithPath = {
  id: string;
  title: string;
  description: string;
  icon: React.ComponentType<any>;
  variant: 'purple' | 'blue' | 'green' | 'orange';
  action?: never; // Action is not needed when path is provided
  path: string;
  comingSoon?: boolean;
};

type ActionCategory = ActionWithDialog | ActionWithPath;

const actionCategories: ActionCategory[] = [
  {
    id: 'team',
    title: 'Team',
    description: 'Manage team members and roles',
    icon: Users,
    path: '/settings',
    variant: 'orange'
  },
  {
    id: 'analytics',
    title: 'Analytics',
    description: 'View detailed portfolio insights',
    icon: BarChart2,
    path: '/analytics',
    variant: 'blue',
    comingSoon: true
  },
  {
    id: 'settings',
    title: 'Settings',
    description: 'Configure your preferences',
    icon: Settings,
    path: '/settings',
    variant: 'green'
  },
  {
    id: 'goals',
    title: 'Investment Goals',
    description: 'Set and track your RIA investment objectives',
    icon: TrendingUp,
    action: 'openDialog',
    variant: 'purple'
  }
];

export const QuickActions = () => {
  const navigate = useNavigate();
  const [showInvestmentGoalsDialog, setShowInvestmentGoalsDialog] = React.useState(false);

  const handleActionClick = (category: ActionCategory) => {
    if (category.comingSoon) {
      return; // Don't handle clicks for coming soon items
    }
    
    if ('action' in category && category.action === 'openDialog') {
      setShowInvestmentGoalsDialog(true);
    } else if ('path' in category && category.path) {
      navigate(category.path);
    }
  };

  const getVariantStyles = (variant: string, comingSoon: boolean = false) => {
    if (comingSoon) {
      return "opacity-50 cursor-not-allowed bg-gray-100 dark:bg-gray-700 transition-all duration-200";
    }
    
    const baseStyles = "transition-all duration-200 hover:scale-105 cursor-pointer ";
    
    const variants = {
      purple: "bg-gradient-to-br from-purple-100 to-purple-200 dark:from-purple-800 dark:to-purple-900 hover:from-purple-200 hover:to-purple-300 dark:hover:from-purple-700 dark:hover:to-purple-800",
      blue: "bg-gradient-to-br from-blue-100 to-blue-200 dark:from-blue-800 dark:to-blue-900 hover:from-blue-200 hover:to-blue-300 dark:hover:from-blue-700 dark:hover:to-blue-800",
      green: "bg-gradient-to-br from-green-100 to-green-200 dark:from-green-800 dark:to-green-900 hover:from-green-200 hover:to-green-300 dark:hover:from-green-700 dark:hover:to-green-800",
      orange: "bg-gradient-to-br from-orange-100 to-orange-200 dark:from-orange-800 dark:to-orange-900 hover:from-orange-200 hover:to-orange-300 dark:hover:from-orange-700 dark:hover:to-orange-800"
    };

    return baseStyles + variants[variant as keyof typeof variants];
  };

  return (
    <Card className="border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-md">
      <CardHeader className="pb-4">
        <CardTitle className="text-lg font-medium text-gray-900 dark:text-white">Quick Actions</CardTitle>
      </CardHeader>
      <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {actionCategories.map((category) => (
          <div
            key={category.id}
            onClick={() => handleActionClick(category)}
            className={cn(
              "rounded-lg p-4 shadow-sm border border-gray-100 dark:border-gray-700 relative",
              getVariantStyles(category.variant, category.comingSoon)
            )}
          >
            {category.comingSoon && (
              <div className="absolute top-2 right-2 bg-gray-600 text-white text-xs px-2 py-1 rounded-full">
                Coming Soon
              </div>
            )}
            <div className="flex items-start space-x-4">
              <div className={cn(
                "p-2 rounded-full",
                category.comingSoon && "bg-gray-300 dark:bg-gray-600 text-gray-600 dark:text-gray-400",
                !category.comingSoon && category.variant === 'purple' && "bg-purple-200 dark:bg-purple-900 text-purple-700 dark:text-purple-300",
                !category.comingSoon && category.variant === 'blue' && "bg-blue-200 dark:bg-blue-900 text-blue-700 dark:text-blue-300",
                !category.comingSoon && category.variant === 'green' && "bg-green-200 dark:bg-green-900 text-green-700 dark:text-green-300",
                !category.comingSoon && category.variant === 'orange' && "bg-orange-200 dark:bg-orange-900 text-orange-700 dark:text-orange-300"
              )}>
                <category.icon className="h-5 w-5" />
              </div>
              <div className="flex-1">
                <h3 className={cn(
                  "font-medium mb-1",
                  category.comingSoon ? "text-gray-500 dark:text-gray-400" : "text-gray-900 dark:text-white"
                )}>
                  {category.title}
                </h3>
                <p className={cn(
                  "text-sm",
                  category.comingSoon ? "text-gray-400 dark:text-gray-500" : "text-gray-700 dark:text-gray-300"
                )}>
                  {category.description}
                </p>
              </div>
            </div>
          </div>
        ))}
      </CardContent>

      {showInvestmentGoalsDialog && (
        <InvestmentGoalsDialog
          open={showInvestmentGoalsDialog}
          onOpenChange={setShowInvestmentGoalsDialog}
        />
      )}
    </Card>
  );
};
